<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { useSubscriptionStore } from '@stores/useSubscriptionStore';
import { trans } from '@elements/translation';
import { useAccountStore } from '@stores/useAccountStore';
import Typography from '@components/Typography.vue';
import { URL_CUSTOMER_PORTAL_CONNECTED } from '@tools/routes/routes';
import { EMBEDDED_COMPONENTS } from '@pages/pay/general/embedded-constants';
import { STRIPE_STATUS } from '@pages/pay/constants/constants';
import ManageOnboarding from './ManageOnboarding.vue';

const isMobile = ref(false);
const step = ref(2);
const accountStore = useAccountStore();
const subscriptionStore = useSubscriptionStore();

const checkIsMobile = () => {
  isMobile.value = window.BREAKPOINTS.isMobile;
};
const nextStep = () => {
  step.value++;
};

const fetchClientSecret = async (accountToken = '') => {
  await subscriptionStore.getOnboardingClientSecret(accountToken);
  return subscriptionStore.onboardingClientSecret;
};

onMounted(async () => {
  if (accountStore.hasBillingInformations && accountStore.stripeAccountStatus === STRIPE_STATUS.ACTIVE) {
    step.value = accountStore.hasBillingInformations ? 3 : 2;
  }

  // @TODO Centralize in pinia store, see  https://bureaux-a-partager.atlassian.net/browse/MATCH-9839
  checkIsMobile();
  window.addEventListener('resize', checkIsMobile);

  if (accountStore.stripeAccountStatus !== STRIPE_STATUS.ACTIVE) {
    step.value = 1;

    try {
      const accountToken = await subscriptionStore.createAccountToken();
      const clientSecret = await fetchClientSecret(accountToken);

      const stripeConnectInstance = await loadConnectAndInitialize({
        publishableKey: window.STRIPE_API_KEY,
        fetchClientSecret: () => clientSecret,
      });

      const onboardingComponent = await stripeConnectInstance.create(
        EMBEDDED_COMPONENTS.ACCOUNT_ONBOARDING,
      );

      onboardingComponent.addEventListener('exit', async () => {
        step.value = accountStore.hasBillingInformations ? 3 : 2;
      });

      const container = document.getElementById('connect-onboarding');
      if (container) {
        container.style.width = '100%';
        container.style.height = '800px';
        container.appendChild(onboardingComponent);
      }
    } catch {
      console.error('Error while openning stripe'); // eslint-disable-line no-console
    }
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkIsMobile);
});
</script>
<template>
  <div class="app-subscription">
    <div v-if="step === 1" class="app-subscription__content stripe-onboarding">
      <div id="connect-onboarding" />
    </div>
    <ManageOnboarding v-if="step === 2" @next-step="nextStep" />
    <div v-if="step === 3" class="invoicing-payment-method">
      <Typography
        variant="body-bold"
      >
        {{ trans('gestion.pay.informations.payment_method.title') }}
      </Typography>

      <Typography
        class="invoicing-payment-method__text"
        variant="body-small"
        color="gray-darker"
      >
        {{ trans('gestion.pay.informations.payment_method.text', { company_name: $companyName }) }}
      </Typography>
      <a :href="URL_CUSTOMER_PORTAL_CONNECTED" target="_blank" class="btn btn--primary">
        {{ trans('gestion.pay.informations.payment_method.button') }}
      </a>
    </div>
  </div>
</template>
